<template>
  <div :class="[
    'flex flex-wrap items-center justify-between gap-2 sm:gap-3',
    variant === 'dashboard' ? 'bg-white px-4 py-3 border-t border-gray-200' : 'mt-6 mb-2'
  ]">
    <!-- Informations sur les entrées -->
    <div class="flex items-center text-xs sm:text-sm text-black">
      <span class="font-medium">
        {{ startIndex + 1 }}-{{ endIndex }} sur {{ totalItems }} {{ itemLabel }}
      </span>
      <span v-if="totalPages > 1" class="ml-2 text-black">
        ({{ currentPage }}/{{ totalPages }})
      </span>
    </div>

    <!-- Contrôles de pagination -->
    <div class="flex items-center gap-1 sm:gap-2 flex-wrap">
      <!-- Sélecteur d'éléments par page -->
      <div v-if="showItemsPerPage" class="hidden sm:flex items-center gap-1">
        <label for="pagination-items-per-page" class="text-xs text-black m-0">Afficher</label>
        <select
          id="pagination-items-per-page"
          :value="itemsPerPage"
          @change="handleItemsPerPageChange"
          class="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
        >
          <option v-for="option in itemsPerPageOptions" :key="option" :value="option">
            {{ option }}
          </option>
        </select>
        <span class="text-xs text-black">par page</span>
      </div>

      <!-- Boutons de navigation -->
      <div class="flex items-center gap-0.5 flex-wrap">
        <!-- Première page -->
        <button
          v-if="showFirstLastButtons"
          @click="goToPage(1)"
          :disabled="currentPage === 1"
          :class="getButtonClasses('nav')"
          title="Première page"
        >
          <i class="fas fa-angle-double-left text-black"></i>
        </button>

        <!-- Page précédente -->
        <button
          @click="previousPage"
          :disabled="currentPage === 1"
          :class="getButtonClasses('nav')"
          title="Page précédente"
        >
          <i class="fas fa-angle-left text-black"></i>
        </button>

        <!-- Pages numérotées -->
        <div class="flex items-center gap-0.5 flex-wrap">
          <button
            v-for="page in visiblePages"
            :key="page"
            @click="goToPage(page)"
            :class="getButtonClasses('page', page === currentPage)"
          >
            {{ page }}
          </button>
        </div>

        <!-- Page suivante -->
        <button
          @click="nextPage"
          :disabled="currentPage === totalPages"
          :class="getButtonClasses('nav')"
          title="Page suivante"
        >
          <i class="fas fa-angle-right text-black"></i>
        </button>

        <!-- Dernière page -->
        <button
          v-if="showFirstLastButtons"
          @click="goToPage(totalPages)"
          :disabled="currentPage === totalPages"
          :class="getButtonClasses('nav')"
          title="Dernière page"
        >
          <i class="fas fa-angle-double-right text-black"></i>
        </button>
      </div>

    </div>

  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  totalItems: number
  currentPage: number
  itemsPerPage: number
  variant?: 'dashboard' | 'product' | 'default'
  itemLabel?: string
  showItemsPerPage?: boolean
  showFirstLastButtons?: boolean
  maxVisiblePages?: number
  itemsPerPageOptions?: number[]
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
  itemLabel: 'entrées',
  showItemsPerPage: true,
  showFirstLastButtons: false,
  maxVisiblePages: 3,
  itemsPerPageOptions: () => [10, 25, 50, 100],
  totalItems: 0,
  currentPage: 1,
  itemsPerPage: 25
})

// Événements
const emit = defineEmits<{
  'update:currentPage'    : [page: number]
  'update:itemsPerPage'   : [itemsPerPage: number]
  'page-change'           : [page: number]
  'items-per-page-change' : [page: number]
}>()

// Computed properties
const totalPages = computed(() => Math.ceil(props.totalItems / props.itemsPerPage))

const startIndex = computed(() => (props.currentPage - 1) * props.itemsPerPage)

const endIndex   = computed(() => Math.min(props.currentPage * props.itemsPerPage, props.totalItems))

const visiblePages = computed(() => {
  const pages: number[] = []
  const halfVisible = Math.floor(props.maxVisiblePages / 2)
  
  let start = Math.max(1, props.currentPage - halfVisible)
  let end = Math.min(totalPages.value, start + props.maxVisiblePages - 1)
  
  // Ajuster le début si on est près de la fin
  if (end - start < props.maxVisiblePages - 1) {
    start = Math.max(1, end - props.maxVisiblePages + 1)
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  
  return pages
})

// Methods
const goToPage = (page: number) => {
  if (page >= 1 && page <= totalPages.value && page !== props.currentPage) {
    emit('update:currentPage', page)
    emit('page-change', page)
  }
}

const previousPage = () => {
  if (props.currentPage > 1) {
    const newPage = props.currentPage - 1
    emit('update:currentPage', newPage)
    emit('page-change', newPage)
  }
}

const nextPage = () => {
  if (props.currentPage < totalPages.value) {
    const newPage = props.currentPage + 1
    emit('update:currentPage', newPage)
    emit('page-change', newPage)
  }
}

const handleItemsPerPageChange = (event: Event) => {
  const target          = event.target as HTMLSelectElement
  const newItemsPerPage = parseInt(target.value)

  // Retourner à la première page lors du changement d'éléments par page
  emit('update:currentPage', 1)
  emit('update:itemsPerPage', newItemsPerPage)
  emit('page-change', 1)
  emit('items-per-page-change', newItemsPerPage)
}

const getButtonClasses = (type: 'nav' | 'page', isActive = false) => {
  const baseClasses = 'transition-colors'
  
  if (type === 'nav') {
    return [
      baseClasses,
      'p-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed',
      'min-w-[32px] h-8 flex items-center justify-center bg-white'
    ]
  }
  
  if (type === 'page') {
    return [
      baseClasses,
      'px-2 py-1.5 text-xs border rounded-md min-w-[32px] h-8 flex items-center justify-center',
      isActive
        ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
        : 'border-gray-300 hover:bg-gray-50 text-black bg-white'
    ]
  }
  
  return baseClasses
}
</script>

<style scoped>
/* Animations pour les transitions de page */
button {
  transition: all 0.2s ease-in-out;
}

button:hover:not(:disabled) {
  transform: translateY(-1px);
}

button:active:not(:disabled) {
  transform: translateY(0);
}
</style> 